import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Checkbox } from "@shared/ui/checkbox";
import { SegmentedControl } from "@shared/components/SegmentedControl";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { X } from "lucide-react";
import type { ParameterRecord } from "./List";
import { PageHeader } from "@core/layouts/PageHeader";
import {
  useCreateParameter,
  useEntityTypeForParameters,
  useSourceModules,
  useUpdateParameter,
  useValuesForSource,
} from "@features/onboarding/utility-setup/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useUtilityServicesOptions } from "@shared/api/hooks";

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

const ENTITY_TYPES = [
  {
    value: "Meter",
    label: "Meter",
    description: "Attached to the physical meter",
  },
  {
    value: "Account",
    label: "Account",
    description: "Attached to the customer account",
  },
  {
    value: "Asset",
    label: "Asset",
    description: "Attached to infrastructure assets",
  },
  {
    value: "Service Point",
    label: "Service Point",
    description: "Attached to the delivery point",
  },
];

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface ParameterFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: ParameterRecord | null;
  onSave: () => void;
}

const defaultValues = {
  name: "",
  status: "Active",
  description: "",
  entity: "",
  dataType: "",
  unit: "",
  allowedValues: [],
  source: "manual",
  sourceModule: "",
  appliesTo: [],
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

const ParameterFormModal: React.FC<ParameterFormModalProps> = ({
  open,
  onOpenChange,
  initialData,
  onSave,
}) => {
  const isEdit = !!initialData;
  const utilityId = useRemoteUtilityId();
  const createMutation = useCreateParameter();
  const updateMutation = useUpdateParameter();
  const queryClient = useQueryClient();
  const { data: utilityServices } = useUtilityServicesOptions(true);
  const { data: entityTypes } = useEntityTypeForParameters();
  const { data: sourceModules } = useSourceModules();
  const [selectedSource, setSelectedSource] = useState<string>(
    initialData?.sourceModule || "",
  );
  const { data: valuesForSource } = useValuesForSource(selectedSource);
  const VALUES_SOURCE_MAP: Record<string, number> = {
    manual: 0,
    external: 1,
  };

  console.log("initialData", initialData);

  // ───────────────────────────────────────────────────────────
  // Fields
  // ───────────────────────────────────────────────────────────

  const fields: FormField[] = [
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
      placeholder: "e.g. Meter Size",
      helperText: "Display name shown in rule builders and the registry.",
      classes: FIELD_CLASSES,
    },
    {
      name: "entity",
      label: "Entity Type",
      type: "select",
      required: true,
      placeholder: "Select entity type...",
      options:
        entityTypes?.map((e: any) => {
          let labelStr = e.value;
          if (typeof labelStr === "object" && labelStr !== null) {
            labelStr =
              labelStr.value ??
              labelStr.name ??
              labelStr.label ??
              JSON.stringify(labelStr);
          }
          return {
            label: String(labelStr),
            value: String(e.key ?? e.id ?? ""),
          };
        }) || [],
      helperText: "Where the parameter is read from at billing time.",
      classes: FIELD_CLASSES,
      isSearchable: false,
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      placeholder: "What this parameter represents and how it should be used.",
      helperText: "What this parameter represents and how it should be used.",
      className: "md:col-span-2",
      classes: FIELD_CLASSES,
    },

    // =========================================================
    // Allowed Values Group
    // =========================================================

    {
      name: "source",
      label: "Source",
      type: "custom",

      group: "Allowed Values",
      groupHelperText:
        "The closed set of valid values for this parameter. Rules can match one or more of these. Leave empty if the parameter accepts any input (e.g. a numeric reading or free text).",
      groupColorNeeded: false,

      helperText: "Where the allowed values come from.",

      className: "col-span-2",

      customRender: (_, form) => (
        <SegmentedControl
          options={[
            {
              label: "Manual list",
              value: "manual",
            },
            {
              label: "Module",
              value: "external",
            },
          ]}
          value={form.watch("source")}
          onChange={(val) => {
            form.setValue("source", val);

            if (val === "manual") {
              form.setValue("sourceModule", "");
            }
          }}
        />
      ),

      classes: FIELD_CLASSES,
    },

    {
      name: "allowedValues",
      label: "Enter Values",
      type: "tags",
      group: "Allowed Values",
      groupColorNeeded: false,
      required: true,
      className: "col-span-2",
      helperText: "Type a value and press Enter to add it to the list.",
      showWhen: (values) => values.source === "manual",
      classes: FIELD_CLASSES,
    },

    {
      name: "sourceModule",
      label: "Source Module",
      type: "select",

      group: "Allowed Values",
      groupColorNeeded: false,

      className: "col-span-2",

      required: true,

      showWhen: (values) => values.source === "external",

      placeholder: "Select a source...",

      options:
        sourceModules?.map((m: any) => {
          let labelStr = m.value;
          if (typeof labelStr === "object" && labelStr !== null) {
            labelStr =
              labelStr.value ??
              labelStr.name ??
              labelStr.label ??
              JSON.stringify(labelStr);
          }
          return {
            label: String(labelStr),
            value: String(m.key ?? m.id ?? ""),
          };
        }) || [],

      classes: FIELD_CLASSES,
    },

    {
      name: "externalPreview",
      type: "custom",

      group: "Allowed Values",
      groupColorNeeded: false,

      className: "col-span-2 -mt-4",

      showWhen: (values) => values.source === "external",

      customRender: (_, form) => {
        const sourceModule = form.watch("sourceModule");

        if (!sourceModule) {
          return (
            <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-3 text-center">
              <p className="text-sm text-gray-500">
                Pick a source above to preview its values
              </p>
            </div>
          );
        }

        let selectedModuleName =
          sourceModules?.find(
            (m: any) => String(m.key) === String(sourceModule),
          )?.value || sourceModule;

        if (
          typeof selectedModuleName === "object" &&
          selectedModuleName !== null
        ) {
          selectedModuleName =
            selectedModuleName.value ??
            selectedModuleName.name ??
            selectedModuleName.label ??
            JSON.stringify(selectedModuleName);
        }

        return (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 ">
            <p className="text-sm font-medium text-gray-600 mb-1">
              Preview values from {String(selectedModuleName)}
            </p>

            <div className="flex flex-wrap gap-1">
              {(valuesForSource || []).length > 0 ? (
                (valuesForSource || []).map((v: any, index: number) => {
                  let displayValue: any = v;
                  let displayKey: any = index;

                  if (typeof v === "object" && v !== null) {
                    displayKey = v.key ?? v.id ?? index;
                    displayValue =
                      v.value ??
                      v.name ??
                      v.label ??
                      v.title ??
                      JSON.stringify(v);

                    // If the extracted value is still an object, extract further or stringify
                    if (
                      typeof displayValue === "object" &&
                      displayValue !== null
                    ) {
                      displayValue =
                        displayValue.value ??
                        displayValue.name ??
                        displayValue.label ??
                        displayValue.title ??
                        JSON.stringify(displayValue);
                    }
                  }

                  return (
                    <Badge
                      key={String(displayKey)}
                      variant="secondary"
                      className="text-xs px-2 py-0.5 font-normal"
                    >
                      {String(displayValue)}
                    </Badge>
                  );
                })
              ) : (
                <span className="text-sm text-gray-500">No values found</span>
              )}
            </div>
          </div>
        );
      },
    },

    // =========================================================
    // Utility Services Group
    // =========================================================

    {
      name: "appliesTo",
      label: "Applies to utility services",
      type: "multi-select",
      className: "col-span-2",
      group: " ",
      groupColorNeeded: false,
      required: true,
      options: utilityServices?.map((s) => ({
        label: s.name,
        value: s.name,
      })),
      helperText: "Select the utility services this parameter applies to.",
      classes: FIELD_CLASSES,
    },
  ];

  // ───────────────────────────────────────────────────────────
  // Form
  // ───────────────────────────────────────────────────────────

  const form = FormService.createForm({
    fields,
    mode: "onChange",

    defaultValues: initialData
      ? {
          ...defaultValues,
          ...initialData,
        }
      : defaultValues,
  });

  // ───────────────────────────────────────────────────────────
  // Reset On Edit
  // ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (initialData) {
      form.reset({
        ...defaultValues,
        ...initialData,
      });
      setSelectedSource(initialData.sourceModule || "");
    } else {
      form.reset(defaultValues);
      setSelectedSource("");
    }
  }, [initialData, open]);

  // ───────────────────────────────────────────────────────────
  // Submit
  // ───────────────────────────────────────────────────────────

  const handleSubmit = (data: any) => {
    let payload: any = {};

    if (isEdit && initialData?.id) {
      // In edit mode, only send the fields that have been modified
      const dirtyFields = form.formState.dirtyFields;
      console.log("dirtyFields", dirtyFields);
      if (dirtyFields.name) payload.name = data.name;
      if (dirtyFields.description) payload.description = data.description || "";
      if (dirtyFields.entity) payload.entity_type = Number(data.entity);
      if (dirtyFields.source)
        payload.values_source = VALUES_SOURCE_MAP[data.source] ?? 0;
      if (dirtyFields.appliesTo)
        payload.applies_to_services = data.appliesTo || [];

      // Handle source related fields if they or the source type itself changed
      if (
        data.source === "manual" &&
        (dirtyFields.allowedValues || dirtyFields.source)
      ) {
        payload.allowed_values = data.allowedValues || [];
      } else if (
        data.source === "external" &&
        (dirtyFields.sourceModule || dirtyFields.source)
      ) {
        payload.values_source_id = data.sourceModule;
        payload.value_source = 1; // 1 for module
        payload.allowed_values = (valuesForSource || []).map((v: any) =>
          typeof v === "object" && v !== null ? v.key : v,
        );
      }

      // If nothing was changed, we can just close the modal early
      if (Object.keys(payload).length === 0) {
        onOpenChange(false);
        return;
      }
    } else {
      // In create mode, send all fields
      payload = {
        name: data.name,
        description: data.description || "",
        entity_type: Number(data.entity),
        values_source: VALUES_SOURCE_MAP[data.source] ?? 0,
        applies_to_services: data.appliesTo || [],
        remote_utility_id: Number(utilityId),
      };

      if (data.source === "manual") {
        payload.allowed_values = data.allowedValues || [];
      } else {
        payload.values_source_id = data.sourceModule;
        payload.allowed_values = (valuesForSource || []).map((v: any) =>
          typeof v === "object" && v !== null ? v.key : v,
        );
      }
    }

    if (isEdit && initialData?.id) {
      updateMutation.mutate(
        { id: initialData.id, payload },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["onboarding", "parameter"],
            });
            onSave();
          },
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["onboarding", "parameter"],
          });
          onSave();
        },
      });
    }
  };

  // ───────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <PageHeader
            className="mb-0"
            title={isEdit ? "Edit Parameter" : "Create Parameter"}
            description="Parameters are reusable attributes that conditional charge rules read from accounts, meters, assets, and service points."
          />
        </DialogHeader>

        <DynamicForm
          fields={fields}
          form={form}
          onSubmit={handleSubmit}
          onFieldChange={(name, value) => {
            if (name === "sourceModule") {
              setSelectedSource(value);
            }
          }}
          config={{
            showSubmitButton: false,
            showResetButton: false,
            classes: FIELD_CLASSES,
            spacing: "small",
          }}
          gridColumns={2}
          responsiveColumns={{
            sm: 1,
            md: 2,
          }}
        />

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>

          <Button
            onClick={form.handleSubmit(handleSubmit)}
            isLoading={
              isEdit ? updateMutation.isPending : createMutation.isPending
            }
          >
            {isEdit ? "Save Changes" : "Create Parameter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ParameterFormModal;
